<template>
    <report-layout
        :report="report"
        :experian_attempt="experian_attempt"
        :show-experian-attempts="true"
        :identity-verified="identity_verified"
        current-step="dashboard"
    >
        <!-- Once the report exists, opening it again is the main thing the
             user comes back for, so it gets a card at the top of the page the
             way the application dashboard does. 86bce6r42: so does a paid
             report whose pull has not happened yet (it failed after payment). -->
        <div v-if="hasReport || awaitingReport" class="mb-4 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200" data-test="report-shortcut">
            <inertia-link :href="route('reports.credit-report', report.id)" class="block p-4 hover:bg-blue-50 rounded-lg">
                <div class="flex items-center justify-between">
                    <div class="flex items-start">
                        <font-awesome-icon :icon="faExternalLinkAlt" class="text-gray-400 mr-2 mt-1" size="sm" />
                        <div>
                            <h2 class="font-bold leading-tight text-blue-600 hover:text-blue-800">{{ hasReport ? 'View My Report' : 'Get My Report' }}</h2>
                            <h2 v-if="hasReport && report.expiry_date" class="text-sm text-gray-600">Expires {{ report.expiry_date }}</h2>
                            <h2 v-else-if="awaitingReport" class="text-sm text-gray-600">Your payment is complete. Open your report.</h2>
                        </div>
                    </div>
                    <div>
                        <font-awesome-icon :icon="faFileMagnifyingGlass" size="2x" class="text-blue-500" />
                    </div>
                </div>
            </inertia-link>
        </div>

        <div class="bg-white rounded-lg shadow-md p-6">
            <h3 class="text-lg font-bold text-gray-900">Report Details</h3>

            <dl class="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3 text-sm">
                <div>
                    <dt class="font-semibold text-gray-700">Report Date</dt>
                    <dd class="text-gray-600">{{ report.formatted_report_date || '—' }}</dd>
                </div>
                <div>
                    <dt class="font-semibold text-gray-700">Expiry Date</dt>
                    <dd class="text-gray-600">{{ report.expiry_date || '—' }}</dd>
                </div>
                <div>
                    <dt class="font-semibold text-gray-700">Paid Amount</dt>
                    <dd class="text-gray-600">
                        <span v-if="report.is_free">Free</span>
                        <span v-else-if="report.paid && report.invoice">${{ report.invoice.charged_amount }}</span>
                        <span v-else>—</span>
                    </dd>
                </div>
            </dl>
        </div>

        <!-- 86bc8uyd2: the Applicant Boost's results, for the renter to download
             and send to a landlord who is not on RentRisk. -->
        <div v-if="report.applicant_boost_paid_at" class="mt-6 bg-white rounded-lg shadow-md p-6" data-test="boost-results">
            <h3 class="flex items-center gap-1.5 text-lg font-bold text-gray-900">
                <font-awesome-icon :icon="faStars" class="text-amber-500" aria-hidden="true" />
                Applicant Boost Results
            </h3>
            <p class="mt-1 text-sm text-gray-600">Download your results and send them to your landlord.</p>

            <ul role="list" class="mt-4 divide-y divide-gray-200 text-sm">
                <li
                    v-for="result in boostResultRows"
                    :key="result.key"
                    class="flex items-center justify-between gap-4 py-3"
                    :data-test="`boost-result-${result.key}`"
                >
                    <span class="font-medium text-gray-900">{{ result.name }}</span>
                    <span v-if="!result.done">
                        <button
                            v-if="result.key === 'identity'"
                            type="button"
                            class="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                            :disabled="verifyingIdentity"
                            data-test="verify-identity"
                            @click="verifyIdentity"
                        >
                            Start
                        </button>
                        <inertia-link v-else :href="result.startHref" class="text-blue-600 hover:text-blue-800">Start</inertia-link>
                    </span>
                    <span v-else-if="result.badge" class="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700">{{ result.badge }}</span>
                    <span v-else-if="!result.href" class="text-gray-500">Being prepared, check back soon</span>
                    <a v-else :href="result.href" target="_blank" class="text-blue-600 hover:text-blue-800">{{ result.linkLabel }}</a>
                </li>
            </ul>
        </div>

        <div class="mt-6 bg-white rounded-md shadow-sm border border-gray-200">
            <div class="grid grid-cols-1 gap-4 p-6 bg-gray-50">
                <template v-for="step in steps" :key="step.key">
                    <boost-identity-card
                        v-if="step.key === 'boost_identity' && !step.complete"
                        :step="step"
                        :busy="verifyingIdentity"
                        @start="verifyIdentity"
                    />
                    <card-header-footer
                        v-else
                        :step_name="step.key"
                        :header="step.name"
                        :body="step.description"
                        :icon="step.icon"
                        :url="step.locked || step.key === 'boost_identity' ? null : step.href"
                        :is-locked="step.locked"
                        :locked_hint="step.lockedHint"
                        :revisitable="!!step.revisitable"
                        :status="stepStatus(step)"
                        :step_number="step.number"
                        :is_next_step="step.isNext"
                        footer="For more information, contact support."
                    />
                </template>
            </div>
        </div>
        <full-page-loader v-if="verifyingIdentity" />
    </report-layout>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faExternalLinkAlt, faStars } from '@fortawesome/pro-solid-svg-icons'
import { faFileMagnifyingGlass } from '@fortawesome/pro-light-svg-icons'

import { router } from '@inertiajs/vue3'
import ReportLayout from './ReportLayout.vue'
import CardHeaderFooter from '@/Shared/CardHeaderFooter.vue'
import FullPageLoader from '@/Shared/FullPageLoader.vue'
import BoostIdentityCard from './Boost/BoostIdentityCard.vue'
import { verifyIdentityWithPlaid } from './Boost/plaidLink'
import { boostResults, buildReportSteps } from './reportSteps'

export default {
    components: {
        ReportLayout,
        CardHeaderFooter,
        FontAwesomeIcon,
        FullPageLoader,
        BoostIdentityCard,
    },
    props: {
        report: Object,
        experian_attempt: Object,
        identity_verified: { type: Boolean, default: false },
        has_income_report: { type: Boolean, default: false },
    },
    data() {
        return {
            faExternalLinkAlt,
            faFileMagnifyingGlass,
            faStars,
            verifyingIdentity: false,
        }
    },
    computed: {
        steps() {
            return buildReportSteps(this.report, { identityVerified: this.identity_verified })
        },
        hasReport() {
            return !!this.report.report_id
        },
        // 86bce6r42: paid for after authentication, but the report is not pulled yet.
        awaitingReport() {
            return !!this.report.pays_to_unlock && !!this.report.paid && !this.report.report_id
        },
        boostResultRows() {
            const done = boostResults(this.report)
            const hasIncomeReport = this.has_income_report
            const assetReport = (this.report.boost_verifications || [])
                .filter((verification) => verification.type === 'assets')
                .pop()

            return [
                {
                    key: 'identity',
                    name: 'Identity Verification',
                    done: done.idv,
                    badge: 'Verified',
                },
                {
                    key: 'income',
                    name: 'Income Verification',
                    done: done.bank_income,
                    href: hasIncomeReport ? route('user-bank-income.load') : null,
                    linkLabel: 'Download PDF',
                    startHref: route('reports.boost.income', this.report.id),
                },
                {
                    key: 'employment',
                    name: 'Employment Verification',
                    done: done.payroll,
                    href: hasIncomeReport ? route('user-payroll-income.load') : null,
                    linkLabel: 'View',
                    startHref: route('reports.boost.employment', this.report.id),
                },
                {
                    key: 'assets',
                    name: 'Asset Verification',
                    done: done.assets,
                    href: assetReport && assetReport.is_asset_report_generated
                        ? route('user-assets.view-report', assetReport.asset_report_token)
                        : null,
                    linkLabel: 'Download PDF',
                    startHref: route('reports.boost.assets', this.report.id),
                },
            ]
        },
    },
    methods: {
        /**
         * 86bc8uyd2: the Applicant Boost's Plaid identity check runs right here
         * rather than on a page of its own.
         */
        async verifyIdentity() {
            try {
                const submitted = await verifyIdentityWithPlaid(this.report.id, {
                    setBusy: (busy) => {
                        this.verifyingIdentity = busy
                    },
                })
                if (submitted) {
                    router.reload()
                }
            } catch (error) {
                console.error('Error:', error.response ? error.response.data : error.message)
            }
        },
        stepStatus(step) {
            if (step.complete) {
                return 'Completed'
            }

            return step.locked ? 'Locked' : 'Ready to Start'
        },
    },
}
</script>
